import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Cloud, RefreshCw, FolderSearch, CheckCircle2, XCircle,
  Film, Tv as TvIcon, AlertTriangle, Loader2, Play, Link2, Search, EyeOff,
  Sparkles,
} from "lucide-react";

export default function AdminLibrary() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reindexing, setReindexing] = useState(false);
  const [unmatched, setUnmatched] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/library/status");
      setStatus(data);
    } catch {
      toast.error("Failed to load library status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const triggerReindex = async () => {
    setReindexing(true);
    try {
      const { data } = await api.post("/library/index/run");
      toast.success(`Reindex done · ${data.matched} matched · ${data.unmatched} unmatched · ${data.pruned} pruned`);
      await reload();
      await loadRecent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reindex failed");
    } finally {
      setReindexing(false);
    }
  };

  const loadUnmatched = async () => {
    try {
      const { data } = await api.get("/library/unmatched", { params: { limit: 200 } });
      setUnmatched(data);
    } catch {
      toast.error("Failed to load unmatched list");
    }
  };

  const [activeSessions, setActiveSessions] = useState(null);
  const loadActive = async () => {
    try {
      const { data } = await api.get("/stream/admin/active");
      setActiveSessions(data || []);
    } catch {
      setActiveSessions([]);
    }
  };
  useEffect(() => { loadActive(); }, []);

  const [recent, setRecent] = useState(null);
  const loadRecent = async () => {
    try {
      const { data } = await api.get("/library/recent-additions", { params: { limit: 50 } });
      setRecent(data);
    } catch {
      setRecent({ items: [] });
    }
  };
  useEffect(() => { loadRecent(); }, []);

  return (
    <section className="mt-10" data-testid="admin-library-section">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Cloud className="w-5 h-5 text-[var(--tt-primary)]" />
          Cloud library &amp; streaming
        </h2>
        <span className="text-[10px] uppercase tracking-wider bg-[var(--tt-primary)]/15 text-[var(--tt-primary)] px-2 py-0.5 rounded font-semibold">
          OpenDrive · 1 stream / user
        </span>
        <button
          onClick={reload}
          disabled={loading}
          data-testid="admin-library-refresh"
          className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-full bg-[var(--tt-surface-2)] hover:bg-white/10 text-[var(--tt-text-muted)] hover:text-white flex items-center gap-1.5"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Setup banner */}
      {status && !status.opendrive_configured && (
        <div className="mb-4 bg-[var(--tt-yellow)]/10 border border-[var(--tt-yellow)]/30 rounded-2xl px-4 py-3 text-xs text-[var(--tt-yellow)] flex items-start gap-3" data-testid="admin-library-setup-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-none" />
          <div>
            <div className="font-bold mb-1">OpenDrive not configured</div>
            <div className="text-white/80 leading-relaxed">
              Set <span className="font-mono-tt bg-black/30 px-1.5 py-0.5 rounded">OPENDRIVE_USERNAME</span> and{" "}
              <span className="font-mono-tt bg-black/30 px-1.5 py-0.5 rounded">OPENDRIVE_PASSWORD</span> in backend env, then redeploy and run a reindex.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="admin-library-stats">
        <Stat icon={CheckCircle2} color="#22c55e" label="Matched" value={status?.matched} />
        <Stat icon={Film} color="#ff2a6d" label="Movies" value={status?.by_type?.movies} />
        <Stat icon={TvIcon} color="#f9a826" label="TV files" value={status?.by_type?.tv} />
        <Stat icon={XCircle} color="#ef4444" label="Unmatched" value={status?.unmatched} />
        <Stat icon={EyeOff} color="#888" label="Hidden" value={status?.hidden} />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5">
          <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
            <FolderSearch className="w-4 h-4 text-[var(--tt-primary)]" /> Re-scan OpenDrive
          </h3>
          <p className="text-[11px] text-[var(--tt-text-muted)] mb-3">
            Walks every folder, parses filenames, locks each one to a TMDB id, and prunes anything removed from the cloud. Safe to run anytime.
          </p>
          <button
            onClick={triggerReindex}
            disabled={reindexing || !status?.opendrive_configured}
            data-testid="admin-library-reindex"
            className="w-full text-sm font-bold px-4 py-2.5 rounded-lg bg-[var(--tt-primary)] text-white hover:bg-[var(--tt-primary-strong)] disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {reindexing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderSearch className="w-4 h-4" />}
            Run reindex
          </button>
        </div>

        <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5">
          <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
            <Play className="w-4 h-4 text-[var(--tt-yellow)]" /> Active streams
          </h3>
          <p className="text-[11px] text-[var(--tt-text-muted)] mb-3">
            Live picture of who&apos;s watching right now (1 per user).
          </p>
          {activeSessions === null ? (
            <div className="text-xs text-[var(--tt-text-muted)] flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
          ) : activeSessions.length === 0 ? (
            <div className="text-xs text-[var(--tt-text-faint)] italic">Nobody streaming.</div>
          ) : (
            <ul className="space-y-1.5" data-testid="admin-library-active-list">
              {activeSessions.map((s) => (
                <li key={s.session_id} className="text-xs bg-[var(--tt-surface-2)] rounded-lg px-3 py-2 flex items-center gap-2 truncate">
                  <span className="text-[10px] font-mono-tt text-[var(--tt-primary)] uppercase">{s.media_type}</span>
                  <span className="font-semibold truncate">{s.title || "—"}</span>
                  <span className="text-[10px] font-mono-tt text-[var(--tt-text-faint)] ml-auto">since {s.started_at?.slice(11, 19)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* New since last scan */}
      {recent && recent.items && recent.items.length > 0 && (
        <div className="bg-[var(--tt-surface)] border border-[var(--tt-primary)]/30 rounded-2xl overflow-hidden mb-4" data-testid="admin-library-recent">
          <div className="px-4 py-3 border-b border-[var(--tt-border)] flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[var(--tt-primary)]" />
            <h3 className="text-sm font-bold">New since last scan</h3>
            <span className="text-[10px] font-mono-tt bg-[var(--tt-primary)]/15 text-[var(--tt-primary)] px-2 py-0.5 rounded font-semibold">
              {recent.items.length}
            </span>
            <span className="text-[10px] text-[var(--tt-text-faint)] font-mono-tt ml-auto">
              since {recent.since?.slice(0, 19).replace("T", " ") || "—"}
            </span>
          </div>
          <ul className="divide-y divide-[var(--tt-border)] max-h-64 overflow-y-auto" data-testid="admin-library-recent-list">
            {recent.items.map((it) => (
              <li key={it.file_id} className="px-4 py-2 text-[11px] font-mono-tt flex items-center gap-3">
                <StatusPill status={it.status} />
                <span className="truncate flex-1 text-[var(--tt-text-muted)]" title={it.path}>
                  {it.title || it.parsed_title || it.path}
                </span>
                <span className="text-[10px] text-[var(--tt-text-faint)] flex-none">
                  {it.first_seen_at?.slice(11, 19) || ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recent runs */}
      {status?.recent_runs?.length ? (
        <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl overflow-hidden mb-4">
          <div className="px-4 py-3 border-b border-[var(--tt-border)] text-sm font-bold">Recent reindex runs</div>
          <ul className="divide-y divide-[var(--tt-border)]" data-testid="admin-library-runs">
            {status.recent_runs.map((r, i) => (
              <li key={i} className="px-4 py-2 text-[11px] font-mono-tt flex items-center gap-3 flex-wrap">
                <span className="text-[var(--tt-text-muted)]">{r.indexed_at?.slice(0, 19).replace("T", " ")}</span>
                <span className="text-emerald-400">{r.matched} matched</span>
                <span className="text-red-400">{r.unmatched} unmatched</span>
                <span className="text-[var(--tt-text-faint)]">· {r.pruned} pruned · {r.videos_seen} videos seen</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Unmatched browser */}
      <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--tt-border)] flex items-center gap-2">
          <h3 className="text-sm font-bold">Unmatched files</h3>
          <button
            onClick={loadUnmatched}
            data-testid="admin-library-load-unmatched"
            className="ml-auto text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[var(--tt-surface-2)] hover:bg-white/10 text-[var(--tt-text-muted)] hover:text-white"
          >
            Load list
          </button>
        </div>
        {unmatched === null ? (
          <div className="py-6 text-center text-[var(--tt-text-faint)] text-sm">Click &quot;Load list&quot; to view unmatched filenames.</div>
        ) : unmatched.length === 0 ? (
          <div className="py-6 text-center text-emerald-400 text-sm flex items-center justify-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Every file is matched.
          </div>
        ) : (
          <ul className="divide-y divide-[var(--tt-border)] max-h-[480px] overflow-y-auto" data-testid="admin-library-unmatched-list">
            {unmatched.map((u) => (
              <UnmatchedRow key={u.file_id} item={u} onMatched={loadUnmatched} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function UnmatchedRow({ item, onMatched }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(item.parsed_title || "");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  // For TV files we default the "apply to whole folder" toggle ON — that's
  // almost always what the admin wants when a show has many unmatched eps.
  const [applyToFolder, setApplyToFolder] = useState(item.media_type === "tv");

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.get("/search", { params: { q: query.trim() } });
      setResults((data?.results || []).slice(0, 8));
    } catch {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const assign = async (tmdbId, mediaType) => {
    setBusy(true);
    try {
      const body = { tmdb_id: tmdbId, media_type: mediaType };
      if (mediaType === "tv" && applyToFolder) body.apply_to_folder = true;
      const { data } = await api.post(`/library/items/${item.file_id}/match`, body);
      if (data.affected > 1) {
        toast.success(`Matched ${data.affected} files → ${data.title}`);
      } else {
        toast.success(`Matched → ${data.title} (${data.year || "—"})`);
      }
      onMatched?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Match failed");
    } finally {
      setBusy(false);
    }
  };

  const hide = async () => {
    if (!window.confirm(`Hide this file from the library?\n\n${item.path}\n\nIt won't appear in the unmatched list anymore and will be skipped on every future reindex. You can unhide it later from the Hidden tab.`)) return;
    setBusy(true);
    try {
      await api.post(`/library/items/${item.file_id}/hide`);
      toast.success("Hidden from library");
      onMatched?.();   // reload list
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hide failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="px-4 py-2 text-[11px] font-mono-tt text-[var(--tt-text-muted)]" data-testid={`admin-library-unmatched-row-${item.file_id}`}>
      <div className="flex items-center gap-2">
        <div className="truncate flex-1" title={item.path}>{item.path}</div>
        <button
          onClick={hide}
          disabled={busy}
          data-testid={`admin-library-hide-${item.file_id}`}
          className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-white/5 text-[var(--tt-text-muted)] hover:bg-white/10 hover:text-white flex items-center gap-1 disabled:opacity-40"
          title="Hide this file from the library — useful when the file isn't a real movie/show"
        >
          <EyeOff className="w-3 h-3" /> Hide
        </button>
        <button
          onClick={() => { setOpen(!open); if (!open && results.length === 0) search(); }}
          data-testid={`admin-library-match-toggle-${item.file_id}`}
          className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-[var(--tt-primary)]/15 text-[var(--tt-primary)] hover:bg-[var(--tt-primary)]/25 flex items-center gap-1"
        >
          <Link2 className="w-3 h-3" /> Match
        </button>
      </div>
      {open && (
        <div className="mt-2 ml-4 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg p-3">
          <div className="flex gap-2 mb-2">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") search(); }}
              data-testid={`admin-library-match-input-${item.file_id}`}
              placeholder="Search TMDB…"
              className="flex-1 bg-black/40 border border-[var(--tt-border)] rounded px-2 py-1.5 text-xs font-sans text-white outline-none focus:border-[var(--tt-primary)]"
            />
            <button
              onClick={search}
              disabled={searching}
              data-testid={`admin-library-match-search-${item.file_id}`}
              className="text-[10px] font-bold uppercase px-2.5 py-1 rounded bg-[var(--tt-primary)] text-white hover:bg-[var(--tt-primary-strong)] disabled:opacity-40 flex items-center gap-1"
            >
              {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />} Search
            </button>
          </div>

          {item.media_type === "tv" && (
            <label
              className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded bg-[var(--tt-primary)]/10 border border-[var(--tt-primary)]/30 cursor-pointer text-[11px] font-sans"
              data-testid={`admin-library-apply-folder-${item.file_id}`}
            >
              <input
                type="checkbox"
                checked={applyToFolder}
                onChange={(e) => setApplyToFolder(e.target.checked)}
                className="accent-[var(--tt-primary)]"
              />
              <span className="text-white">
                Apply to <span className="font-bold text-[var(--tt-primary)]">every episode</span> in this show&apos;s folder
              </span>
            </label>
          )}
          {results.length > 0 && (
            <ul className="space-y-1 font-sans">
              {results.map((r) => (
                <li key={`${r.media_type}-${r.id}`}>
                  <button
                    onClick={() => assign(r.id, r.media_type)}
                    disabled={busy}
                    data-testid={`admin-library-match-pick-${item.file_id}-${r.id}`}
                    className="w-full text-left flex items-center gap-3 px-2 py-2 rounded hover:bg-white/5 text-xs disabled:opacity-40 border border-transparent hover:border-[var(--tt-primary)]/30 transition-colors"
                  >
                    {r.poster ? (
                      <img
                        src={r.poster}
                        alt=""
                        loading="lazy"
                        className="w-[44px] h-[66px] rounded object-cover bg-black/40 border border-[var(--tt-border)] flex-none"
                      />
                    ) : (
                      <div className="w-[44px] h-[66px] rounded bg-black/40 border border-[var(--tt-border)] flex-none flex items-center justify-center">
                        {r.media_type === "tv"
                          ? <TvIcon className="w-4 h-4 text-[var(--tt-text-faint)]" />
                          : <Film className="w-4 h-4 text-[var(--tt-text-faint)]" />}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-white font-semibold truncate">{r.title}</div>
                      <div className="text-[10px] text-[var(--tt-text-muted)] line-clamp-2 mt-0.5">
                        {r.overview || "—"}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-none">
                      <span className="text-[9px] uppercase font-bold text-[var(--tt-primary)] bg-[var(--tt-primary)]/15 px-1.5 py-0.5 rounded">{r.media_type}</span>
                      <span className="text-[10px] text-[var(--tt-text-faint)]">{r.year || "—"}</span>
                      <span className="text-[10px] text-[var(--tt-yellow)]">★ {(r.rating || 0).toFixed(1)}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

function Stat({ icon: Icon, color, label, value }) {
  return (
    <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-4" data-testid={`admin-library-stat-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)] mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color }} /> {label}
      </div>
      <div className="font-display text-2xl font-extrabold text-white tabular-nums">{value ?? "—"}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    matched:    { color: "text-emerald-400 bg-emerald-500/15", label: "matched" },
    unmatched:  { color: "text-red-400 bg-red-500/15",         label: "unmatched" },
    hidden:     { color: "text-[var(--tt-text-faint)] bg-white/5", label: "hidden" },
  };
  const s = map[status] || map.unmatched;
  return (
    <span className={`text-[9px] uppercase font-bold px-2 py-0.5 rounded ${s.color} flex-none`}>
      {s.label}
    </span>
  );
}
